import { useContext } from "react";
import { useController } from "react-hook-form";
import type { RoleName } from "common/authorization";
import type { PathArray } from "common/utils/formStructure";
import { AuthContext } from "../../../../../../../Authorization/AuthContext";
import { Button } from "../../../../../../../Common/Buttons/Button";
import type { IconNames } from "../../../../../../../Common/Icons/LucideIcons";
import { Tooltip } from "../../../../../../../Common/Tooltip";
import { getUiSchemaPropertyFormPath } from "../../../form/form";
import { getInputOptionFromFieldPermissions } from "../../../utils";
import {
  VIEWER_TYPE,
  viewerTypeToFieldPermissionsMap,
} from "../../../viewerType";

type ToggleViewerPermissionsProps = {
  fieldPath: PathArray;
  isReadOnly: boolean;
};

export function ToggleViewerPermissions({
  fieldPath,
  isReadOnly,
}: ToggleViewerPermissionsProps) {
  const viewOnRetrievePropertyFormPath = getUiSchemaPropertyFormPath(
    fieldPath,
    "viewOnRetrieve",
  );

  const { field } = useController({
    name: viewOnRetrievePropertyFormPath,
  });

  const { account } = useContext(AuthContext);
  const publicPortalEnabled = account?.publicPortal.enabled;

  const permissions = field.value as Array<RoleName> | undefined;
  const option = getInputOptionFromFieldPermissions(
    permissions ?? [],
    viewerTypeToFieldPermissionsMap,
  );

  const toggleViewerPermissions = () => {
    const updatedInputOption =
      option === VIEWER_TYPE.TEAM ?
        VIEWER_TYPE.TEAM_AND_GUESTS
      : VIEWER_TYPE.TEAM;
    const updatedPermissions =
      viewerTypeToFieldPermissionsMap[updatedInputOption];

    field.onChange(updatedPermissions);
  };

  const iconMap: Record<VIEWER_TYPE, IconNames> = {
    [VIEWER_TYPE.TEAM]: "eye-off",
    [VIEWER_TYPE.TEAM_AND_GUESTS]: "eye",
  };

  const icon = option ? iconMap[option] : null;
  const verb = option === VIEWER_TYPE.TEAM ? "on" : "off";
  const tooltipText =
    option === VIEWER_TYPE.TEAM_AND_GUESTS ?
      "Visible to guests after submitted"
    : "Hidden from guests after submitted";

  if (!icon) {
    return null;
  }

  return (
    <Tooltip
      popoverContent={tooltipText}
      content={
        <div>
          <Button
            variant="secondary"
            size="small"
            onPress={toggleViewerPermissions}
            aria-label={`Toggle ${verb} public viewer permissions`}
            data-testid={`${fieldPath}_toggle-viewer-permissions`}
            isDisabled={!publicPortalEnabled || isReadOnly}
            icon={icon}
          />
        </div>
      }
    />
  );
}
